import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import './Auth.css';

const NORIS_LOGO = 'https://noris.in/Assets/N%20logo.webp';

/* ── password strength ─────────────────────────── */
function pwStrength(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const SCOLOR = ['#e53e3e', '#e53e3e', '#f6a623', '#2d9e5f', '#1a6fad'];
const SLABEL_KEYS = ['auth.strengthVeryWeak', 'auth.strengthWeak', 'auth.strengthFair', 'auth.strengthGood', 'auth.strengthStrong'];

const AUTH_MODE = { LOGIN: 'login', REGISTER: 'register', FORGOT: 'forgot' };
const REG_STEPS = { INFO: 1, VERIFY: 2, PASSWORD: 3 };
const FORGOT_STEPS = { EMAIL: 1, VERIFY: 2, RESET: 3 };

export default function AuthPage() {
  // The login / registration / password-reset screens always render in English,
  // regardless of the language selected elsewhere in the app. We resolve keys
  // with a fixed English translator instead of the active-language `t`.
  const { i18n } = useTranslation();
  const tr = i18n.getFixedT('en');
  const { login } = useAuth();
  const [mode, setMode] = useState(AUTH_MODE.LOGIN);
  const [regStep, setRegStep] = useState(REG_STEPS.INFO);
  const [forgotStep, setForgotStep] = useState(FORGOT_STEPS.EMAIL);

  const [form, setF] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    phone: '', station_name: '', station_address: '',
    station_gstin: '', station_state: '',
    otp: ''
  });

  const [showPw, setSP] = useState(false);
  const [loading, setL] = useState(false);
  const [error, setE] = useState('');
  const [success, setS] = useState('');
  const [timer, setTimer] = useState(0);
  const [lockout, setLockout] = useState(0);

  // ── Lockout Timer Effect ──
  useEffect(() => {
    let interval = null;
    if (lockout > 0) {
      interval = setInterval(() => setLockout(l => l - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [lockout]);

  // ── Timer Effect ──
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => setTimer(t => t - 1), 1000);
    } else if (timer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const strength = pwStrength(form.password);
  const f = k => e => setF({ ...form, [k]: e.target.value });

  /* ── Handlers ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setL(true); setE(''); setS('');
    try {
      const data = await api.auth.login({ email: form.email, password: form.password });
      setS(tr('auth.toasts.loginSuccess'));
      // Give a moment for the success message to be seen
      setTimeout(() => login(data), 1000);
    } catch (err) {
      setE(err.message || tr('auth.errors.loginFailed'));
      if (err.data?.remaining_seconds) {
        setLockout(err.data.remaining_seconds);
      }
    }
    setL(false);
  };

  const handleRegisterInfo = async (e) => {
    e.preventDefault();
    setL(true); setE(''); setS('');
    try {
      await api.auth.register({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        station_name: form.station_name.trim(),
        station_address: form.station_address.trim(),
        station_gstin: form.station_gstin.trim().toUpperCase(),
        station_state: form.station_state.trim(),
      });
      setS(tr('auth.toasts.otpSent'));
      setRegStep(REG_STEPS.VERIFY);
      setTimer(300);
    } catch (err) { setE(err.message || tr('auth.errors.registrationFailed')); }
    setL(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setL(true); setE(''); setS('');
    try {
      await api.auth.verifyOtp({ email: form.email, otp: form.otp });
      setS(tr('auth.toasts.otpVerified'));
      if (mode === AUTH_MODE.REGISTER) setRegStep(REG_STEPS.PASSWORD);
      else setForgotStep(FORGOT_STEPS.RESET);
    } catch (err) { setE(err.message || tr('auth.errors.verificationFailed')); }
    setL(false);
  };

  const handleResend = async () => {
    if (timer > 0) return;
    setL(true); setE(''); setS('');
    try {
      await api.auth.resendOtp({ email: form.email });
      setS(tr('auth.toasts.newOtpSent'));
      setTimer(300);
    } catch (err) { setE(err.message || tr('auth.errors.resendFailed')); }
    setL(false);
  };

  const handleCompleteReg = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setE(tr('auth.errors.passwordsMismatch')); return; }
    setL(true); setE(''); setS('');
    try {
      const data = await api.auth.completeRegistration({
        email: form.email, otp: form.otp, password: form.password
      });
      setS(tr('auth.toasts.accountCreated'));
      setTimeout(() => login(data), 1500);
    } catch (err) { setE(err.message || tr('auth.errors.failedToSetPassword')); }
    setL(false);
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setL(true); setE(''); setS('');
    try {
      await api.auth.forgotPassword({ email: form.email });
      setS(tr('auth.toasts.resetOtpSent'));
      setForgotStep(FORGOT_STEPS.VERIFY);
      setTimer(1800);
    } catch (err) { setE(err.message || tr('auth.errors.failedToSendOtp')); }
    setL(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setE(tr('auth.errors.passwordsMismatch')); return; }
    setL(true); setE(''); setS('');
    try {
      await api.auth.resetPassword({ email: form.email, otp: form.otp, password: form.password });
      setS(tr('auth.toasts.resetSuccess'));
      setTimeout(() => { setMode(AUTH_MODE.LOGIN); setS(''); setForgotStep(FORGOT_STEPS.EMAIL); }, 1500);
    } catch (err) { setE(err.message || tr('auth.errors.resetFailed')); }
    setL(false);
  };

  return (
    <div className="auth-container-root">
      <div className="auth-card-wrapper">
        
        {/* Top Tab Navigation */}
        {mode !== AUTH_MODE.FORGOT && (
          <div className="auth-top-tabs">
            <button 
              className={`auth-top-tab-btn tab-signin ${mode === AUTH_MODE.LOGIN ? 'active' : ''}`}
              onClick={() => { setMode(AUTH_MODE.LOGIN); setE(''); setS(''); }}
            >
              🔐 {tr('auth.signIn')}
            </button>
            <button 
              className={`auth-top-tab-btn tab-register ${mode === AUTH_MODE.REGISTER ? 'active' : ''}`}
              onClick={() => { setMode(AUTH_MODE.REGISTER); setE(''); setS(''); }}
            >
              📋 {tr('auth.registerStation')}
            </button>
          </div>
        )}

        <div className="auth-card-body-split">
          {/* LEFT PANEL */}
          <div className="auth-split-left-panel">
            <div className="left-panel-content">
              <div className="left-panel-brand">
                <div className="brand-logo-box">
                  <img src={NORIS_LOGO} alt="Noris" onError={e => { e.target.style.display = 'none'; }} />
                </div>
                <div className="brand-text-box">
                  <span className="brand-title">Noris Petro ERP</span>
                  <span className="brand-subtitle">by noris.in</span>
                </div>
              </div>

              <div className="left-panel-hero">
                <h2 className="hero-heading">{tr('auth.heroHeading')}</h2>
                <p className="hero-text">
                  {tr('auth.heroText')}
                </p>
              </div>

              {/* Styled Step Rows with Blue Circular SVG Icons */}
              <div className="left-panel-steps">
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="9" y1="9" x2="15" y2="9"></line><line x1="9" y1="13" x2="15" y2="13"></line><line x1="9" y1="17" x2="15" y2="17"></line></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.dailySales')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3"></path></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.tankStock')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 0 1 10 10c0 5.523-4.477 10-10 10S2 17.523 2 12A10 10 0 0 1 12 2z"></path><path d="M12 6v6l4 2"></path></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.meterReadings')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.gstBilling')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.purchaseExpense')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.employeeMgmt')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line><line x1="2" y1="20" x2="22" y2="20"></line></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.financialDashboards')}</span>
                </div>
                <div className="left-step-row">
                  <div className="step-circle-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                  </div>
                  <span className="step-label-text">{tr('auth.features.exports')}</span>
                </div>
              </div>
            </div>

            {/* Vector Fuel Pump Watermark */}
            <svg className="left-panel-pump-watermark" width="140" height="180" viewBox="0 0 24 28" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="12" height="18" rx="2" ry="2"></rect>
              <rect x="5" y="8" width="8" height="5"></rect>
              <circle cx="9" cy="18" r="2"></circle>
              <path d="M15 9h2a2 2 0 0 1 2 2v8a2 2 0 0 0 4 0v-8a2 2 0 0 0-2-2h-2"></path>
              <path d="M6 24h6"></path>
            </svg>

            <div className="left-panel-footer">
              <span>© 2025 Noris Solutions</span>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="auth-split-right-panel">
            
            {/* Form Headers */}
            <div className="right-panel-header">
              <h1 className="form-main-title">
                {mode === AUTH_MODE.LOGIN && `${tr('auth.welcomeBack')} 🔑`}
                {mode === AUTH_MODE.REGISTER && `${tr('auth.registerYourStation')} ⛽`}
                {mode === AUTH_MODE.FORGOT && `${tr('auth.resetYourPassword')} ✉️`}
              </h1>
              <p className="form-main-subtitle">
                {mode === AUTH_MODE.LOGIN && tr('auth.loginSubtitle')}
                {mode === AUTH_MODE.REGISTER && tr('auth.registerSubtitle')}
                {mode === AUTH_MODE.FORGOT && tr('auth.forgotSubtitle')}
              </p>
            </div>

            {/* Error/Success Alerts */}
            <div className="auth-notifications">
              {error && (
                <div className="auth-alert-message alert-error-box">
                  <span className="alert-icon">⚠️</span>
                  <div className="alert-text-content">
                    {error}
                    {lockout > 0 && (
                      <div className="lockout-time-alert">
                        ⏱️ {tr('auth.tryAgainIn')} <strong>{formatTime(lockout)}</strong>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {success && (
                <div className="auth-alert-message alert-success-box">
                  <span className="alert-icon">✅</span>
                  <div className="alert-text-content">{success}</div>
                </div>
              )}
            </div>

            <div className="right-panel-form-container">
              {/* LOGIN FORM */}
              {mode === AUTH_MODE.LOGIN && (
                <form onSubmit={handleLogin} className="auth-form-el">
                  <div className="auth-form-group">
                    <label>{tr('auth.emailAddress')} *</label>
                    <input
                      className="auth-form-input"
                      type="email"
                      placeholder="you@station.in"
                      value={form.email}
                      onChange={f('email')}
                      required
                    />
                  </div>

                  <div className="auth-form-group">
                    <div className="auth-label-row">
                      <label>{tr('auth.password')} *</label>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a 
                        onClick={() => { setMode(AUTH_MODE.FORGOT); setE(''); setS(''); }} 
                        className="auth-forgot-link"
                      >
                        {tr('auth.forgot')}
                      </a>
                    </div>
                    <div className="auth-password-input-wrap">
                      <input 
                        className="auth-form-input" 
                        type={showPw ? 'text' : 'password'} 
                        placeholder="••••••••" 
                        value={form.password} 
                        onChange={f('password')} 
                        required 
                      />
                      <button 
                        type="button" 
                        className="auth-pw-toggle-btn" 
                        onClick={() => setSP(!showPw)}
                      >
                        {showPw ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  <div className="auth-options-row">
                    <label className="auth-checkbox-container">
                      <input type="checkbox" className="auth-checkbox-box" />
                      <span>{tr('auth.rememberDevice')}</span>
                    </label>
                  </div>

                  <button type="submit" className="auth-btn-submit" disabled={loading}>
                    {loading ? (
                      <span className="btn-spinner-group">
                        <span className="btn-spinner-ring"></span>
                        {tr('auth.signingIn')}
                      </span>
                    ) : (
                      `🚀 ${tr('auth.signIn')}`
                    )}
                  </button>
                </form>
              )}

              {/* REGISTER FORM */}
              {mode === AUTH_MODE.REGISTER && (
                <div className="register-wizard-box">
                  {regStep === REG_STEPS.INFO && (
                    <form onSubmit={handleRegisterInfo} className="auth-form-el">
                      <div className="auth-form-section-title"><span>{tr('auth.ownerDetails')}</span></div>

                      <div className="auth-form-grid-row">
                        <div className="auth-form-group">
                          <label>{tr('auth.fullName')} *</label>
                          <input className="auth-form-input" type="text" placeholder={tr('auth.fullNamePlaceholder')} value={form.name} onChange={f('name')} required />
                        </div>
                        <div className="auth-form-group">
                          <label>{tr('auth.phone')} *</label>
                          <input className="auth-form-input" type="tel" placeholder={tr('auth.phonePlaceholder')} value={form.phone} onChange={f('phone')} required />
                        </div>
                      </div>

                      <div className="auth-form-group">
                        <label>{tr('auth.emailAddress')} *</label>
                        <input className="auth-form-input" type="email" placeholder="station@example.com" value={form.email} onChange={f('email')} required />
                      </div>

                      <div className="auth-form-section-title"><span>{tr('auth.stationDetails')}</span></div>

                      <div className="auth-form-group">
                        <label>{tr('auth.stationName')} *</label>
                        <input className="auth-form-input" type="text" placeholder="e.g. Sri Lakshmi Filling Station" value={form.station_name} onChange={f('station_name')} required />
                      </div>

                      <div className="auth-form-grid-row">
                        <div className="auth-form-group">
                          <label>GSTIN</label>
                          <input className="auth-form-input" type="text" placeholder="36XXXXX1234X1ZX" value={form.station_gstin} onChange={f('station_gstin')} maxLength={15} />
                        </div>
                        <div className="auth-form-group">
                          <label>{tr('auth.state')}</label>
                          <select className="auth-form-select" value={form.station_state} onChange={f('station_state')}>
                            <option value="">{tr('auth.selectOption')}</option>
                            {['Andhra Pradesh', 'Telangana', 'Karnataka', 'Tamil Nadu', 'Maharashtra', 'Other'].map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="auth-form-group">
                        <label>{tr('auth.stationAddress')}</label>
                        <input
                          className="auth-form-input"
                          type="text"
                          placeholder={tr('auth.stationAddressPlaceholder')}
                          value={form.station_address}
                          onChange={f('station_address')} 
                        />
                      </div>

                      <button type="submit" className="auth-btn-submit" disabled={loading}>
                        {loading ? (
                          <span className="btn-spinner-group">
                            <span className="btn-spinner-ring"></span>
                            {tr('auth.processing')}
                          </span>
                        ) : (
                          `🚀 ${tr('auth.nextVerifyEmail')} →`
                        )}
                      </button>
                    </form>
                  )}

                  {regStep === REG_STEPS.VERIFY && (
                    <form onSubmit={handleVerifyOtp} className="auth-form-el otp-align-form">
                      <div className="otp-prompt-box">
                        <label className="otp-heading-label">{tr('auth.enterOtpSentTo')}</label>
                        <span className="otp-destination-email">{form.email}</span>
                        
                        <div className="otp-input-field-wrap">
                          <input 
                            className="auth-form-input otp-large-box" 
                            type="text" 
                            placeholder="000000"
                            value={form.otp} 
                            onChange={f('otp')} 
                            maxLength={6} 
                            required 
                          />
                        </div>
                        
                        <div className="otp-time-expiration">
                          {tr('auth.expiresIn')} <span className={`otp-time-countdown ${timer < 30 ? 'critical' : ''}`}>{formatTime(timer)}</span>
                        </div>
                      </div>

                      <button type="submit" className="auth-btn-submit" disabled={loading}>
                        {loading ? (
                          <span className="btn-spinner-group">
                            <span className="btn-spinner-ring"></span>
                            {tr('auth.verifying')}
                          </span>
                        ) : (
                          tr('auth.verifyContinue')
                        )}
                      </button>

                      <div className="otp-resend-control">
                        {timer > 0 ? (
                          <span className="otp-resend-wait">{tr('auth.resendCodeIn', { time: formatTime(timer) })}</span>
                        ) : (
                          <button type="button" onClick={handleResend} className="otp-resend-btn" disabled={loading}>
                            {tr('auth.resendOtpCode')}
                          </button>
                        )}
                      </div>
                    </form>
                  )}

                  {regStep === REG_STEPS.PASSWORD && (
                    <form onSubmit={handleCompleteReg} className="auth-form-el">
                      <div className="auth-form-section-title"><span>{tr('auth.securityCredentials')}</span></div>

                      <div className="auth-form-group">
                        <label>{tr('auth.createPassword')} *</label>
                        <input className="auth-form-input" type="password" placeholder="••••••••" value={form.password} onChange={f('password')} required />
                        
                        {form.password && (
                          <div className="pw-strength-container-bar">
                            <div className="pw-strength-track">
                              <div 
                                className="pw-strength-fill" 
                                style={{ width: `${(strength + 1) * 20}%`, background: SCOLOR[strength] }} 
                              />
                            </div>
                            <span className="pw-strength-text-label" style={{ color: SCOLOR[strength] }}>
                              {tr('auth.strength')} <strong>{tr(SLABEL_KEYS[strength])}</strong>
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="auth-form-group">
                        <label>{tr('auth.confirmPassword')} *</label>
                        <input className="auth-form-input" type="password" placeholder={tr('auth.reenterPassword')} value={form.confirmPassword} onChange={f('confirmPassword')} required />
                      </div>

                      <button type="submit" className="auth-btn-submit" disabled={loading}>
                        {loading ? (
                          <span className="btn-spinner-group">
                            <span className="btn-spinner-ring"></span>
                            {tr('auth.completing')}
                          </span>
                        ) : (
                          `🚀 ${tr('auth.createAccount')}`
                        )}
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* FORGOT PASSWORD FORM */}
              {mode === AUTH_MODE.FORGOT && (
                <div className="forgot-wizard-box">
                  {forgotStep === FORGOT_STEPS.EMAIL && (
                    <form onSubmit={handleForgotRequest} className="auth-form-el">
                      <div className="auth-form-group">
                        <label>{tr('auth.enterRegisteredEmail')} *</label>
                        <input className="auth-form-input" type="email" placeholder="you@station.in" value={form.email} onChange={f('email')} required />
                      </div>
                      
                      <button type="submit" className="auth-btn-submit" disabled={loading}>
                        {loading ? (
                          <span className="btn-spinner-group">
                            <span className="btn-spinner-ring"></span>
                            {tr('auth.sending')}
                          </span>
                        ) : (
                          tr('auth.sendResetOtp')
                        )}
                      </button>
                    </form>
                  )}

                  {forgotStep === FORGOT_STEPS.VERIFY && (
                    <form onSubmit={handleResetPassword} className="auth-form-el">
                      <div className="otp-prompt-box">
                        <label className="otp-heading-label">{tr('auth.enterOtpSentToShort')}</label>
                        <span className="otp-destination-email">{form.email}</span>
                        
                        <div className="otp-input-field-wrap">
                          <input 
                            className="auth-form-input otp-large-box" 
                            type="text" 
                            placeholder="000000"
                            value={form.otp} 
                            onChange={f('otp')} 
                            maxLength={6} 
                            required 
                          />
                        </div>
                        
                        <div className="otp-time-expiration">
                          {tr('auth.expiresIn')} <span className={`otp-time-countdown ${timer < 60 ? 'critical' : ''}`}>{formatTime(timer)}</span>
                        </div>
                      </div>

                      <div className="auth-form-group">
                        <label>{tr('auth.newPassword')} *</label>
                        <input className="auth-form-input" type="password" placeholder="••••••••" value={form.password} onChange={f('password')} required />
                        
                        {form.password && (
                          <div className="pw-strength-container-bar">
                            <div className="pw-strength-track">
                              <div 
                                className="pw-strength-fill" 
                                style={{ width: `${(strength + 1) * 20}%`, background: SCOLOR[strength] }} 
                              />
                            </div>
                            <span className="pw-strength-text-label" style={{ color: SCOLOR[strength] }}>
                              {tr('auth.strength')} <strong>{tr(SLABEL_KEYS[strength])}</strong>
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="auth-form-group">
                        <label>{tr('auth.confirmNewPassword')} *</label>
                        <input className="auth-form-input" type="password" placeholder={tr('auth.reenterPassword')} value={form.confirmPassword} onChange={f('confirmPassword')} required />
                      </div>

                      <button type="submit" className="auth-btn-submit" disabled={loading}>
                        {loading ? (
                          <span className="btn-spinner-group">
                            <span className="btn-spinner-ring"></span>
                            {tr('auth.resetting')}
                          </span>
                        ) : (
                          tr('auth.resetPassword')
                        )}
                      </button>

                      <div className="otp-resend-control">
                        {timer > 0 ? (
                          <span className="otp-resend-wait">{tr('auth.resendCodeIn', { time: formatTime(timer) })}</span>
                        ) : (
                          <button type="button" onClick={handleForgotRequest} className="otp-resend-btn" disabled={loading}>
                            {tr('auth.resendOtpCode')}
                          </button>
                        )}
                      </div>
                    </form>
                  )}

                  <div className="forgot-footer-back">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a 
                      onClick={() => { setMode(AUTH_MODE.LOGIN); setForgotStep(FORGOT_STEPS.EMAIL); setE(''); setS(''); }}
                      className="forgot-back-link"
                    >
                      ← {tr('auth.backToLogin')}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Alternative Links (Sign In <-> Register Switcher) */}
            {mode !== AUTH_MODE.FORGOT && (
              <div className="auth-bottom-switcher">
                {mode === AUTH_MODE.REGISTER ? (
                  <span>
                    {tr('auth.alreadyRegistered')} {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a onClick={() => { setMode(AUTH_MODE.LOGIN); setE(''); setS(''); }}>{tr('auth.signIn')} →</a>
                  </span>
                ) : (
                  <span>
                    {tr('auth.newToNoris')} {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a onClick={() => { setMode(AUTH_MODE.REGISTER); setE(''); setS(''); }}>{tr('auth.registerStation')} →</a>
                  </span>
                )}
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}
